// Cross-origin MFA verification hint.
//
// The PeptideClients suite uses central auth (`auth.peptideclients.com`)
// for sign-in + MFA challenge. After a successful MFA verify, the user
// is handed off via URL fragment to the destination B2B app at
// `<app>/auth/callback#access_token=…&refresh_token=…?return_to=/…
// &mfa_verified=1`. The destination app's per-SPA `RequireMfaVerified`
// guard ALSO checks MFA state. The cross-origin handoff has a short
// window during which the server-RPC fallback in `readMfaClaims()`
// can race against the just-rotated session: the auth-origin Supabase
// session is gone, the app-origin session is in place, but the
// `core.user_mfa_sessions` row's `auth_session_id` is being looked up
// against the in-flight JWT, and the gate trips the wrong way exactly
// once per handoff. Symptom from the operator: "I verify on
// auth.peptideclients.com, then immediately end up at /mfa on
// app.peptideclients.com — over and over."
//
// This module provides a same-tab, same-origin hint:
//
//   1. The auth site's `buildHandoffUrl` is asked to add
//      `?mfa_verified=1` when (and only when) it has just confirmed
//      that the session has cleared MFA in this turn.
//   2. The destination app's `AuthCallbackPage` calls
//      `writeMfaLocalHint(sessionId, ttlMs)` immediately after
//      `supabase.auth.setSession()` lands and the URL still carries
//      the hint. The hint is stored in `localStorage` keyed by JWT
//      session_id with a 12-hour TTL (matches `user_mfa_sessions.
//      expires_at`).
//   3. `RequireMfaVerified` calls `readMfaLocalHint(sessionId)` BEFORE
//      the network RPC. When the hint exists for the current
//      session_id and hasn't expired, the gate allows immediately.
//      The hint is non-authoritative: sensitive-action surfaces still
//      re-check MFA via the server, and an attacker who can write to
//      the destination app's localStorage already controls that
//      origin (in which case the user is hosed regardless).
//
// SAFETY: keyed by the JWT's `session_id`, NOT by the user id. A
// stolen access_token that the attacker shoves into localStorage will
// not match this hint unless the attacker ALSO controls the auth-
// origin handoff (in which case they already have the token in their
// own origin's localStorage anyway). The hint expires server-side and
// client-side at the same 12-hour window.

const MFA_LOCAL_HINT_KEY = 'pc.mfa.local_verified.v1';
const DEFAULT_TTL_MS = 12 * 60 * 60 * 1000;

interface MfaLocalHintRecord {
  /** JWT `session_id` claim. Hint only applies to this specific session. */
  session_id: string;
  /** Unix ms at which the hint expires. */
  until_ms: number;
}

interface MfaLocalHintStore {
  v: 1;
  hints: MfaLocalHintRecord[];
}

function safeRead(): MfaLocalHintStore {
  if (typeof window === 'undefined') return { v: 1, hints: [] };
  try {
    const raw = window.localStorage.getItem(MFA_LOCAL_HINT_KEY);
    if (!raw) return { v: 1, hints: [] };
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      (parsed as { v?: number }).v !== 1 ||
      !Array.isArray((parsed as { hints?: unknown }).hints)
    ) {
      return { v: 1, hints: [] };
    }
    return parsed as MfaLocalHintStore;
  } catch {
    return { v: 1, hints: [] };
  }
}

function safeWrite(store: MfaLocalHintStore): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(MFA_LOCAL_HINT_KEY, JSON.stringify(store));
  } catch {
    // hardened browsers / private mode — silently ignore
  }
}

/**
 * Record that the auth site has just confirmed MFA for the given
 * `session_id` (or that MFA is not required for this account). The
 * destination app's `RequireMfaVerified` will trust this hint for the
 * lifetime of the matching `user_mfa_sessions` row (~12h).
 *
 * Pruning happens lazily — every read drops expired entries — so
 * `localStorage` doesn't grow unbounded across many sessions.
 */
export function writeMfaLocalHint(sessionId: string, ttlMs: number = DEFAULT_TTL_MS): void {
  if (!sessionId || typeof sessionId !== 'string') return;
  const now = Date.now();
  const until_ms = now + Math.max(60_000, ttlMs);
  const store = safeRead();
  const next: MfaLocalHintStore = {
    v: 1,
    hints: [
      // Drop any prior hint for this session_id and any expired rows
      // before appending the fresh one.
      ...store.hints.filter((h) => h.session_id !== sessionId && h.until_ms > now),
      { session_id: sessionId, until_ms },
    ],
  };
  safeWrite(next);
}

/**
 * Read the hint for `sessionId`. Returns null when missing/expired.
 * Also opportunistically prunes expired rows on read so the store
 * stays small.
 */
export function readMfaLocalHint(sessionId: string | null | undefined): { until_ms: number } | null {
  if (!sessionId || typeof sessionId !== 'string') return null;
  if (typeof window === 'undefined') return null;
  const now = Date.now();
  const store = safeRead();
  let pruned = false;
  const live = store.hints.filter((h) => {
    if (h.until_ms <= now) {
      pruned = true;
      return false;
    }
    return true;
  });
  if (pruned) safeWrite({ v: 1, hints: live });
  const hit = live.find((h) => h.session_id === sessionId);
  return hit ? { until_ms: hit.until_ms } : null;
}

/**
 * Clear the hint for `sessionId`. Called when the user signs out from
 * the destination app or explicitly disables MFA — both invalidate
 * the prior trust statement.
 */
export function clearMfaLocalHint(sessionId: string | null | undefined): void {
  if (!sessionId || typeof sessionId !== 'string') return;
  if (typeof window === 'undefined') return;
  const store = safeRead();
  const next = store.hints.filter((h) => h.session_id !== sessionId);
  if (next.length !== store.hints.length) {
    safeWrite({ v: 1, hints: next });
  }
}

/**
 * Wipe every hint, regardless of session_id. Belt-and-suspenders for
 * sign-out flows that don't have the session_id handy.
 */
export function clearAllMfaLocalHints(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(MFA_LOCAL_HINT_KEY);
  } catch {
    // ignore
  }
}
